import {
  Box,
  ButtonGroup,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
} from "@chakra-ui/react";
import React from "react";
import { useForm } from "react-hook-form";

import { Button, useToast } from "../../../components";
import {
  CurrentUserDocument,
  CurrentUserFragment,
  CurrentUserQuery,
  useVerifyEmailSignInMutation,
} from "../../graphql";

interface VerifySignInByEmailFormProps {
  email: string;
  onSignIn: (currentUser: CurrentUserFragment) => void;
  onResendVerification: () => void;
}

const VerifySignInByEmailForm: React.FC<VerifySignInByEmailFormProps> = ({
  email,
  onSignIn,
  onResendVerification,
}) => {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{
    verificationCode: string;
  }>();

  const [verifySignIn, { loading }] = useVerifyEmailSignInMutation({
    update(cache, { data }) {
      const currentUser = data?.verifyEmailSignIn?.currentUser;
      if (currentUser) {
        cache.writeQuery<CurrentUserQuery>({
          query: CurrentUserDocument,
          data: { currentUser },
        });
      }
    },
    onCompleted: ({ verifyEmailSignIn }) => {
      const currentUser = verifyEmailSignIn?.currentUser;
      if (!currentUser) {
        return;
      }
      onSignIn(currentUser);
    },
    onError: (err) => {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 30000,
      });
    },
  });

  const onSubmit = handleSubmit(({ verificationCode }): void => {
    verifySignIn({
      variables: { email, verificationCode },
    });
  });

  return (
    <form onSubmit={onSubmit}>
      <Flex direction="column" alignItems="center">
        <Box mb={4}>
          <>
            An email with a verification code was sent to{" "}
            <strong>{email}</strong>.
          </>
        </Box>
        <FormControl
          id="verificationCode"
          width="160px"
          isRequired
          isInvalid={errors.verificationCode !== undefined}
        >
          <FormLabel>Verification code</FormLabel>
          <Input
            {...register("verificationCode")}
            autoFocus
            autoComplete="one-time-code"
            minLength={6}
            maxLength={6}
          />
          {errors.verificationCode !== undefined && (
            <FormErrorMessage>
              {errors.verificationCode.message}
            </FormErrorMessage>
          )}
        </FormControl>
        <ButtonGroup spacing={4} mt={6} mx="auto">
          <Button type="submit" isLoading={loading}>
            Sign in
          </Button>
          <Button
            variant="outline"
            onClick={onResendVerification}
            isDisabled={loading}
          >
            Try again
          </Button>
        </ButtonGroup>
      </Flex>
    </form>
  );
};

export default VerifySignInByEmailForm;
